import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { SaveDataHandler } from 'src/modules/app-template/models/save-data-handler.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableCellEvent } from 'src/modules/sm-base/models/table-cell-event.model';
import { TableCell } from 'src/modules/sm-base/models/table-cell.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { TableSortColumn } from 'src/modules/sm-base/models/table-sort-column.model';
import { ToolTipDefinition } from 'src/modules/sm-base/models/ToolTipDefinition.model';
import { ViewState } from 'src/modules/sm-base/models/view-state.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Strings } from 'src/modules/utils/shared/strings';
import { Utils } from 'src/modules/utils/shared/utils';
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { Competency } from '../../shared/competency.model';
import { EnaioCertificatesTools } from '../../shared/enaio-certificates-tools';
import { FinalGradeHistory } from '../../shared/final-grade-history.entity';
import { FinalGradeModification } from '../../shared/final-grade-modification.dto';
import { FinalGrade } from '../../shared/final-grade.entity';
import { GlobalSettings } from '../../shared/global-settings.entity';
import { GradeBookSchoolClass } from '../../shared/grade-book-school-class.entity';
import { Pupil } from '../../shared/pupil.model';
import { SchoolClass } from '../../shared/school-class.model';
import { SchoolTypeSettings } from '../../shared/school-type-settings.entity';
import { FinalExamMatrixComponent } from '../final-exam-matrix/final-exam-matrix.component';

@Component({
    selector: 'app-class-final-exams',
    templateUrl: './class-final-exams.component.html',
    styleUrls: ['./class-final-exams.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClassFinalExamsComponent extends ComponentView {

    service = inject(EnaioCertificateService);

    year: number;

    schoolClasses: SchoolClass[];
    schoolClassesLastYear: SchoolClass[];
    pupils: Pupil[];
    domains: string[];
    gradeBookSchoolClasses: GradeBookSchoolClass[];
    finalGrades: FinalGrade[];
    modifications: FinalGradeModification[]= [];
    grades: OrdinaryObject<number>;
    schoolClassIdByPupilId: OrdinaryObject<number> = {};
    history: FinalGradeHistory[];
    table: TableData;
    historyTable: TableData;

    activeTabIndex = "0";
    compact = false;

    getInitialViewState(): ViewState {
        return new ViewState(true);
    }

    override async initParams(): Promise<boolean> {
        this.year = EnaioCertificatesTools.getCurrentYear();
        this.schoolClasses = (await this.service.restGetSchoolClasses(EnaioCertificatesTools.getSchoolYearFromYear(this.year))).filter(sc => sc.isForFinalExams());
        this.schoolClassesLastYear = (await this.service.restGetSchoolClasses(EnaioCertificatesTools.getSchoolYearFromYear(this.year - 1))).filter(sc => sc.isForFinalExamsPrevYear());

        let globalSettings = await RestEndpoint.main().find({}).run("api/cer/globalsettings").get(GlobalSettings);

        this.modifications = [];
        this.pupils = [];
        this.gradeBookSchoolClasses = [];
        this.grades = {};
        this.schoolClassIdByPupilId = {};

        let competencies: Competency[] = [];
        for (let schoolClass of this.schoolClasses) {
            let classPupils = (await this.service.restGetPupils(schoolClass.id)).filter(p => p.isForFinalExam());
            for (let pupil of classPupils) {
                this.schoolClassIdByPupilId[pupil.id] = schoolClass.id;
            }
            this.pupils = [...this.pupils, ...classPupils];

            let gradeBookSchoolClass = await RestEndpoint.main().find({ schoolClassId: schoolClass.id, isHalfYear: 0 }).run("api/cer/gradebookschoolclass").get(GradeBookSchoolClass);
            this.gradeBookSchoolClasses.push(gradeBookSchoolClass);

            let settings = await RestEndpoint.main().find({schoolType: schoolClass.getSchoolType(), year: this.year, isHalfYear: false}).run("api/cer/schooltypesettings").get(SchoolTypeSettings);
            Objects.addMissingFields(this.grades, gradeBookSchoolClass.getPupilAverageMap(globalSettings, settings, schoolClass, null, classPupils));

            competencies = [...competencies, ...await RestEndpoint.main().find({ schoolClassId: schoolClass.id }).run("api/cer/competency").list(Competency)
            ];                        
        }

        for (let schoolClass of this.schoolClassesLastYear) {
            let gradeBookSchoolClass = await RestEndpoint.main().find({ schoolClassId: schoolClass.id, isHalfYear: 0 }).run("api/cer/gradebookschoolclass").get(GradeBookSchoolClass);

            let settings = await RestEndpoint.main().find({schoolType: schoolClass.getSchoolType(), year: this.year, isHalfYear: false}).run("api/cer/schooltypesettings").get(SchoolTypeSettings);
            let avg = gradeBookSchoolClass.getPupilAverageMap(globalSettings, settings, schoolClass, null, this.pupils);
            for (let key of Objects.getPropertyNames(avg)) {
                if (key.endsWith("|Astronomie")) {
                    this.grades[key] = avg[key];
                }
            }
        }
        for (let key of Objects.getPropertyNames(this.grades)) {
            if (this.grades[key] != null) {
                this.grades[key] = Utils.truncateDecimals(this.grades[key], 1);
            }
        }

        this.finalGrades = await RestEndpoint.main().find({ year: this.year }).run("api/cer/finalgrade").list(FinalGrade);

        this.domains = await this.service.restGetFinalExamDomains(Arrays.getUnique(competencies.map(c => c.domain)));

        this.fillTable();

        this.app.updateNavigation(null, [
            this.service.getHomeClassRegister(),
            { label: "Jahres- und Prüfungsnoten", routerLink: ["/enaio-certificates", "class-registers", "final-exams"] }
        ]);

        this.viewState.loading = false;
        return true;
    }

    getDomainStyleClass(d: string): string {
        return "xbg-" + (d == "Deutsch" ? "red-100" : d == "Englisch" ? "yellow-100" : d == "Mathematik" ? "blue-100" : "green-100");
    }

    valueChanged(e: TableCellEvent): void {
        let col = e.getTableColumn();
        this.valueChangedInternal((e.row.raw as Pupil).id,col.additional?.domain as string, 
            col.additional?.field as
                | "writtenGrade" | "oralGrade" | "finalGrade" | "note" | "floor",e.value);
    }

    valueChangedInternal(pupilId: number, domain: string, modifiedField:
            | "writtenGrade" | "oralGrade" | "finalGrade" | "note" | "floor", value: any): void {
        if (modifiedField != null) {
            let finalGrade = this.finalGrades.find(fg => fg.pupilId == pupilId && fg.domain == domain);
            let modification = this.modifications.find(m => m.item.pupilId == pupilId && m.item.domain == domain);

            let modified = modification != null ? modification.item : finalGrade != null ? Objects.cloneDeep(finalGrade) : new FinalGrade();
            modified.pupilId = pupilId;
            modified.domain = domain;
            modified.year = this.year;

            modification ??= Arrays.addThrough(this.modifications, new FinalGradeModification());
            modification.item = modified;

            modification.changedFields.push(modifiedField);
            modification.changedFields = Arrays.getUnique(modification.changedFields);
            if (modifiedField == "writtenGrade") {
                modification.item.writtenGrade = value;
            }
            else if (modifiedField == "oralGrade") {
                modification.item.oralGrade = value;
            }
            else if (modifiedField == "finalGrade") {
                modification.item.finalGrade = value;
            }
            else if (modifiedField == "note") {
                modification.item.note = value;
            }
            else if (modifiedField == "floor") {
                modification.item.floor = value;
            }
        }

        this.fillTable();
    }

    fillTable(): void {
        let cols = [
            new TableColumn("class", "Klasse", TableCellType.string, 5, {
                frozen: "left"    
            }),
            new TableColumn("lastName", "Nachname", TableCellType.link, 15, {
                frozen: "left"   
            }),
            new TableColumn("firstName", "Vorname", TableCellType.string, 15, {
                frozen: "left"    
            }),
            new TableColumn("sex", "Geschlecht", TableCellType.string, 3, {
                frozen: "left",
                rotate90: true
            }),
            new TableColumn("eligible", "Prüfungszulassung", TableCellType.string, 3, {
                frozen: "left",
                rotate90: true,
                align: "center",
                getStyleClassCallback: cell => cell.value === "!" ? "xbg-red-100 text-red-400 bold" : null
            }),
            new TableColumn("note", "Bemerkung", TableCellType.string, 30, {
                editable: true,
                additional: {
                    field: "note"
                }
            }),
        ];

        for (let domain of this.domains) {
            if (this.compact) {
                cols.push(new TableColumn("domain_" + domain + "_finalGrade", domain, TableCellType.decimal, 3, {
                    rotate90: true,
                    numDecimalDigits: 1,
                    expandDecimalDigits: true,
                    align: "center",
                    additional: {
                        domain
                    }
                }));
            }
            else {
                cols = [
                    ...cols, 
                    new TableColumn("domain_" + domain + "_yearGrade", "Jahresnote", TableCellType.decimal, 3, {
                        groupCaption: domain,
                        rotate90: true,
                        numDecimalDigits: 1,
                        expandDecimalDigits: true,
                        align: "center",
                        additional: {
                            domain
                        }
                    }),
                    new TableColumn("domain_" + domain + "_writtenExamGrade", "sch. Prüf.", TableCellType.decimal, 3, {
                        groupCaption: domain,
                        rotate90: true,
                        numDecimalDigits: 1,

                        expandDecimalDigits: true,
                        editable: true,
                        align: "center",
                        additional: {
                            domain,
                            field: "writtenGrade"
                        }                
                    }),
                    new TableColumn("domain_" + domain + "_oralExamGrade", "mdl. Prüf.", TableCellType.decimal, 3, {
                        groupCaption: domain,
                        rotate90: true,
                        numDecimalDigits: 1,
                        expandDecimalDigits: true,
                        editable: true,
                        align: "center",
                        additional: {
                            domain,
                            field: "oralGrade"
                        }                
                    }),
                    new TableColumn("domain_" + domain + "_totalExamGrade", "Ergb.", TableCellType.decimal, 3, {
                        groupCaption: domain,
                        rotate90: true,
                        numDecimalDigits: 1,
                        expandDecimalDigits: true,
                        align: "center",
                        additional: {
                            domain
                        }                
                    }),
                    new TableColumn("domain_" + domain + "_finalGrade", "Endnote", TableCellType.decimal, 3, {
                        groupCaption: domain,
                        rotate90: true,
                        numDecimalDigits: 1,
                        expandDecimalDigits: true,
                        align: "center",                
                        getStyleClassCallback: cell => {
                            let grade = cell.row.values["domain_" + domain + "_totalExamGrade"] as number;
                            let five = grade != null && Utils.formatNumber(grade, 1, true).endsWith("5");
                            return this.getDomainStyleClass(domain) + (five ? " mouse-pointer" : "");
                        },
                        editable: true,
                        editableCallback: cell => {
                            let grade = cell.row.values["domain_" + domain + "_totalExamGrade"] as number;
                            let five = grade != null && Utils.formatNumber(grade, 1, true).endsWith("5");
                            return five;
                        },
                        editor: cell => {
                            let grade = cell.row.values["domain_" + domain + "_totalExamGrade"] as number;
                            let five = grade != null && Utils.formatNumber(grade, 1, true).endsWith("5");
                            return five ? new FrontendField("", "", FrontendFieldType.dropdown, {
                                listItems: FrontendFieldListItem.make([Math.floor(grade), Math.ceil(grade)])
                            }) : null;
                        },
                        setValue: cell => {
                            let grade = cell.row.values["domain_" + domain + "_totalExamGrade"] as number;
                            let value = cell.value as number;
                            let floor = value == Math.floor(grade);
                            cell.row.values["domain_" + domain + "_finalGrade"] = value;
                            cell.row.values["domain_" + domain + "_floor"] = floor;
                            this.valueChangedInternal((cell.row.raw as Pupil).id, domain, "floor", floor);
                        },
                        additional: {
                            domain,
                            field: "finalGrade"
                        }
                    })
                ];
            }
        }

        cols = [
            ...cols,
            new TableColumn("totalGp1", "GPs", TableCellType.decimal, 3, {
                groupCaption: "Gesamtprädikat",
                rotate90: true,
                numDecimalDigits: 1,
                expandDecimalDigits: true,
                align: "center",
                frozen: "right"
            }),
            new TableColumn("totalGp2", "GP2", TableCellType.decimal, 3, {
                groupCaption: "Gesamtprädikat",
                rotate90: true,
                numDecimalDigits: 1,
                expandDecimalDigits: true,
                align: "center",
                frozen: "right"
            }),
            new TableColumn("totalEgp", "EGP", TableCellType.decimal, 3, {
                groupCaption: "Gesamtprädikat",
                rotate90: true,
                numDecimalDigits: 1,
                expandDecimalDigits: true,
                align: "center",
                frozen: "right"
            }),
            new TableColumn("totalGp", "GP", TableCellType.string, 15, {
                groupCaption: "Gesamtprädikat",
                rotate90: true,
                numDecimalDigits: 1,
                frozen: "right"
            })
        ];

        let rows = this.table?.rows ?? this.pupils.map(item => { let result = new TableRow("", item, { class: item.getClassDisplayName(), lastName: new TableCell(item.lastName, { toolTip: new ToolTipDefinition(null, "Berechnungsmatrix öffnen") }), firstName: item.firstName, sex: Strings.getSubstring(item.sex, 0, 1), eligible: this.isEligibleForFinalExam(item), note: this.finalGrades.find(fg => fg.pupilId == item.id && fg.domain == "")?.note ?? "" }); for (let domain of this.domains) { result.values["domain_" + domain + "_yearGrade"] = this.grades[item.id + "|" + domain]; let fg = this.finalGrades.find(fg2 => fg2.pupilId == item.id && fg2.domain == domain); result.values["domain_" + domain + "_oralExamGrade"] = fg?.oralGrade; result.values["domain_" + domain + "_writtenExamGrade"] = fg?.writtenGrade; result.values["domain_" + domain + "_floor"] = fg?.floor; } return result; });

        for (let row of rows) {
            let totalGp1 = 0;
            let totalGp1Count = 0;
            let totalGp2 = 0;
            let totalGp2Count = 0;

            for (let domain of this.domains) {
                let oralExamGrade = row.values["domain_" + domain + "_oralExamGrade"] as number;
                let writtenExamGrade = row.values["domain_" + domain + "_writtenExamGrade"] as number;
                let yearGrade = row.values["domain_" + domain + "_yearGrade"] as number;
                let examGrade = writtenExamGrade == null ? oralExamGrade == null ? null : oralExamGrade : oralExamGrade == null ? writtenExamGrade : (oralExamGrade + writtenExamGrade) / 2;
                let total = yearGrade == null ? null : Utils.truncateDecimals(examGrade == null ? yearGrade : 0.6 * yearGrade + 0.4 * examGrade, 1);
                row.values["domain_" + domain + "_totalExamGrade"] = total;
                let dotFive = Math.floor(Math.abs(total) * 10) % 10 == 5;
                row.values["domain_" + domain + "_finalGrade"] = total != null ? dotFive && row.values["domain_" + domain + "_floor"] ? Math.floor(total) : Math.round(total) : null;

                if (total != null) {
                    if (this.isMainDomain(domain)) {
                        totalGp1 += total;
                        totalGp1Count++;
                    }
                    else {
                        totalGp2 += total;
                        totalGp2Count++;
                    }
                }

                row.values.totalGp1 = totalGp1Count == 0 ? null : totalGp1 / totalGp1Count;
                row.values.totalGp2 = totalGp2Count == 0 ? null : totalGp2 / totalGp2Count;
                row.values.totalEgp = totalGp1Count == 0 ? totalGp2Count == 0 ? null : row.values.totalGp2 : totalGp2Count == 0 ? row.values.totalGp1 : (row.values.totalGp1 + row.values.totalGp2) / 2;   
                row.values.totalGp = EnaioCertificatesTools.getFinalExamGp(row.values.totalEgp as number);
            }            
        }

        this.table = new TableData(cols, rows, ["class", "lastName", "firstName"]);
    }

    isMainDomain(domain: string): boolean {
        return domain == "Deutsch" || domain == "Englisch" || domain == "Mathematik";
    }

    isEligibleForFinalExam(pupil: Pupil): string {
        for (let domain of this.domains) {
            let g = this.grades[pupil.id + "|" + domain];
            if (this.isMainDomain(domain) && g == null) {
                return "";
            }
        }
        for (let domain of this.domains) {
            let g = this.grades[pupil.id + "|" + domain];
            if (g >= 4.5) {
                return "!";
            }
        }
        return "ja";
    }

    override getCanDeactivateMessage(): string {
        return this.isChanged() ? "" : null;
    }

    isChanged(): boolean {
        return !Utils.isNoe(this.modifications);
    }

    async save(): Promise<void> {
        if (this.isChanged()) {
            if ((await this.app.saveDataHandler2(new SaveDataHandler({
                worker: async () => RestEndpoint.main().post().query({ year: this.year }).body(this.modifications).run("api/cer/finalgrade").getText(),
                componentView: this
            }))).ok) {
                await this.initParams();
            }
        }    
    }

    async activeTabChanged(): Promise<void> {
        if (this.activeTabIndex == "1") {
            this.history = await RestEndpoint.main().find({ year: this.year }).run("api/cer/finalgrade/history").list(FinalGradeHistory);  

            this.historyTable = new TableData([
                new TableColumn("date", "Datum/Uhrzeit", TableCellType.dateTime, 1),
                new TableColumn("user", "Benutzer", TableCellType.string, 20),
                new TableColumn("pupil", "Schüler", TableCellType.string, 20),
                new TableColumn("domain", "Fach", TableCellType.string, 20),
                new TableColumn("modification", "Änderung", TableCellType.string, -1)
            ], this.history.map(h => new TableRow("", h, {
                date: h.changeDate,
                user: h.changedBy,
                pupil: this.pupils.find(p => p.id == h.pupilId)?.getFullName() ?? "<" + h.pupilId + ">",
                domain: h.domain,
                modification: h.modification
            })), [new TableSortColumn("date", false)]);

        }
    }

    async cellClicked(event: TableCellEvent): Promise<void> {
        if (event.column.colId == "lastName") {
            let pupilId = (event.row.raw as Pupil).id;
            await this.app.navigateToComponent(FinalExamMatrixComponent, { pupilId, schoolClassId: this.schoolClassIdByPupilId[pupilId] });
        }
        else if (event.column.colId.endsWith("_finalGrade")) {
            let domain = "";
            let grade = event.row.values["domain_" + domain + "_totalExamGrade"] as number;
            let five = grade != null && Utils.formatNumber(grade, 1, true).endsWith("5");
        }
    }

}
